import React, { Component } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';

import {
  LackPermissions,
  TestCycleDetails,
  TestCycleDetailsPageNavigationBanner,
  TestCycleHeader,
  TestCycleTestCasesListTab,
} from '@qality/common-components';
import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { VersionSelectOption } from '@qality/common-components/src/select/VersionSelect';
import { TestCaseFilterProvider } from '@qality/common-components/src/test-cycle/TestCaseFilter';
import { TestCasesInCycleListProvider } from '@qality/common-components/src/test-cycle/testCasesInCycleList/TestCaseInCycleListProvider';
import TestCycleDetailsPageBulkActionsBar from '@qality/common-components/src/test-cycle/testCasesInCycleList/TestCycleDetailsPageBulkActionsBar';
import { messages } from '@qality/common-messages';
import { TestCaseInCycle, TestCycle } from '@qality/common-model';
import {
  ExecutionStatusService,
  FlagService,
  IssueService,
  StorageOfUniqueServices,
  TestCyclesService,
} from '@qality/common-service';
import { ExecutionStatuses } from '@qality/common-service/src/ExecutionStatusService';
import {
  ExecutionStatusUtils,
  StringUtils,
  TestCycleUtils,
} from '@qality/common-utils';

import './_test-cycles.scss';

type Props = Record<string, never>;

type State = {
  testCycle?: TestCycle;
  lackPermissions: boolean;
  selectedTestCasesIds: Array<string>;
  executionStatuses: ExecutionStatuses;
};

export default class TestCycleDetailsPage extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      testCycle: null,
      lackPermissions: false,
      selectedTestCasesIds: [],
      executionStatuses: {
        allStatuses: [],
        defaultStatus: null,
      },
    };
  }

  createBulkImportSuccessFlag = (
    addedTestCyclesCount: number,
    testCycle: TestCycle
  ): void => {
    if (
      addedTestCyclesCount &&
      Number.isInteger(Number(addedTestCyclesCount))
    ) {
      FlagService.createSuccessFlag(
        'Success',
        `${addedTestCyclesCount} QAlity Tests have been successfully added to the ${StringUtils.escapeHtmlToTextNode(
          testCycle.name
        )}`
      );
    }
  };

  async componentDidMount(): Promise<void> {
    await StorageOfUniqueServices.get('HostNameService').init();
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getJiraURLSearchParams();
    const testCycleId = urlSearchParams.get('testCycleId', '');
    const addedTestCyclesCount = urlSearchParams.get('addedTC', '');
    const [testCycle, executionStatuses] = await Promise.all([
      TestCyclesService.fetchTestCycle(testCycleId),
      ExecutionStatusService.getStatuses(),
    ]);
    const updatedParams = [];

    for (const pair of urlSearchParams.entries()) {
      if (pair[0] === 'addedTC') {
        updatedParams.push({ name: pair[0], value: pair[1], omit: true });
      } else {
        updatedParams.push({ name: pair[0], value: pair[1] });
      }
    }

    this.createBulkImportSuccessFlag(addedTestCyclesCount, testCycle);

    StorageOfUniqueServices.get('URLService').updateUrlParams(updatedParams);

    if (!testCycle) {
      this.setState({
        lackPermissions: true,
      });
    } else {
      this.setState({
        testCycle,
        executionStatuses,
      });
    }
  }

  updateTestCycleComment = async (value: string): Promise<void> => {
    try {
      const updatedTestCycle = cloneDeep(this.state.testCycle);
      updatedTestCycle.comment = value;
      this.setState({
        testCycle: updatedTestCycle,
      });
      TestCyclesService.updateTestCycle(
        this.state.testCycle.id,
        updatedTestCycle.getRequestData()
      );
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['testCycle.details.update.error']
      );
    }
  };

  handleDueDateChange = async (value: string): Promise<void> => {
    try {
      const updatedTestCycle = cloneDeep(this.state.testCycle);
      updatedTestCycle.dueDate = value;
      this.setState({
        testCycle: updatedTestCycle,
      });
      TestCyclesService.updateTestCycle(
        this.state.testCycle.id,
        updatedTestCycle.getRequestData()
      );
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['testCycle.details.update.error']
      );
    }
  };

  handleVersionChange = async (value: VersionSelectOption): Promise<void> => {
    try {
      const updatedTestCycle = cloneDeep(this.state.testCycle);
      updatedTestCycle.version = value.value
        ? {
            id: value.value,
            name: value.label,
          }
        : null;
      this.setState({
        testCycle: updatedTestCycle,
      });
      TestCyclesService.updateTestCycle(
        this.state.testCycle.id,
        updatedTestCycle.getRequestData()
      );
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['testCycle.details.update.error']
      );
    }
  };

  saveTestCycleName = async (value: string): Promise<void> => {
    try {
      const updatedTestCycle = cloneDeep(this.state.testCycle);
      updatedTestCycle.name = value;
      this.setState({
        testCycle: updatedTestCycle,
      });
      TestCyclesService.updateTestCycle(
        this.state.testCycle.id,
        updatedTestCycle.getRequestData()
      );
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['testCycle.details.update.error']
      );
    }
  };

  addTestCase = async (selectedIssuesIds: Array<string>): Promise<void> => {
    if (!this.state.testCycle) {
      return;
    }

    const testCyclesService = StorageOfUniqueServices.get(
      'TestCyclesServiceUnique'
    );
    const addToCycleResult = await testCyclesService.addToCycle(
      selectedIssuesIds,
      this.state.testCycle.id
    );

    if (!addToCycleResult) {
      return;
    }

    const newTestCases = await Promise.all(
      addToCycleResult.data.map(async (testCase) => {
        const testCaseIssue = await IssueService.getIssue(testCase.testCaseId);

        return {
          id: testCase.id,
          testCaseId: testCase.testCaseId,
          statusDetail: testCase.statusDetail,
          testCase: testCaseIssue,
          testExecution: testCase.testExecution,
          testType: testCase.testType,
        };
      })
    );

    const updatedTestCycle = cloneDeep(this.state.testCycle);

    updatedTestCycle.status =
      ExecutionStatusUtils.updateSummaryWithAddedStatuses(
        updatedTestCycle.status,
        newTestCases.map((newTestCase) => newTestCase.statusDetail)
      );
    updatedTestCycle.testCases.push(...newTestCases);
    updatedTestCycle.numberOfTestCases += newTestCases.length;

    this.setState({
      testCycle: updatedTestCycle,
    });
  };

  updateTestCycle = (updatedTestCycle: TestCycle): void => {
    this.setState({
      testCycle: updatedTestCycle,
    });
  };

  selectTestCases = (updatedSelectedTestCasesIds: Array<string>): void => {
    this.setState({
      selectedTestCasesIds: updatedSelectedTestCasesIds,
    });
  };

  removeTestCase = async (testCase: TestCaseInCycle): Promise<void> => {
    try {
      const updatedTestCycle = cloneDeep(this.state.testCycle);

      const removedStatus = testCase.statusDetail;

      updatedTestCycle.testCases = updatedTestCycle.testCases.filter(
        (testCaseItem) => testCaseItem.id !== testCase.id
      );
      updatedTestCycle.numberOfTestCases--;
      updatedTestCycle.status =
        ExecutionStatusUtils.updateSummaryWithRemovedStatuses(
          updatedTestCycle.status,
          [removedStatus]
        );

      this.setState({
        testCycle: updatedTestCycle,
      });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['']
      );
    }
  };

  render(): JSX.Element {
    StorageOfUniqueServices.get('AP').sizeToParent(true);
    if (this.state.testCycle) {
      return (
        <div>
          <TestCaseFilterProvider>
            <TestCasesInCycleListProvider
              testCaseActions={{
                removeTestCase: this.removeTestCase,
                addTestCases: this.addTestCase,
                selectTestCases: this.selectTestCases,
              }}
              testCycleActions={{ updateTestCycle: this.updateTestCycle }}
              testCycle={this.state.testCycle}
              selectedTestCasesIds={this.state.selectedTestCasesIds}
              executionStatuses={this.state.executionStatuses}
            >
              <TestCycleDetailsPageNavigationBanner />
              <div className='test-cycle-details-page'>
                <TestCycleHeader
                  testCycle={this.state.testCycle}
                  canEdit={this.state.testCycle.canEdit}
                  saveTestCycleName={this.saveTestCycleName}
                />
                <Tabs id='test-cycle-details-page-tabs-id'>
                  <TabList>
                    <Tab>Test Cases</Tab>
                    <Tab>Overview</Tab>
                  </TabList>

                  <TabPanel>
                    <TestCycleTestCasesListTab />
                  </TabPanel>
                  <TabPanel>
                    <TestCycleDetails
                      testCycle={this.state.testCycle}
                      canEdit={this.state.testCycle.canEdit}
                      updateTestCycleComment={this.updateTestCycleComment}
                      handleDueDateChange={this.handleDueDateChange}
                      handleVersionChange={this.handleVersionChange}
                      updateTestCycle={this.updateTestCycle}
                    />
                  </TabPanel>
                </Tabs>
              </div>
              <TestCycleDetailsPageBulkActionsBar
                canEdit={this.state.testCycle.canEdit}
                projectKey={this.state.testCycle.project.key}
                hasAnyLinkedIssues={TestCycleUtils.isAnyIssueLinkedToAnyExecution(
                  this.state.testCycle
                )}
              />
            </TestCasesInCycleListProvider>
          </TestCaseFilterProvider>
        </div>
      );
    }

    if (this.state.lackPermissions) {
      return <LackPermissions />;
    }

    return <Loading relative />;
  }
}
